import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Mail, Calendar, MapPin, Sparkles, ArrowRight, Copy, Gift } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatEUR } from "@/lib/api";

export default function BookingSuccess() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");
    const [booking, setBooking] = useState(null);
    const [payStatus, setPayStatus] = useState(sessionId ? "checking" : null);

    useEffect(() => {
        api.get(`/bookings/${id}`).then((res) => setBooking(res.data)).catch(() => {});
    }, [id]);

    useEffect(() => {
        if (!sessionId) return;
        let attempts = 0;
        const poll = async () => {
            attempts += 1;
            try {
                const r = await api.get(`/payments/checkout/status/${sessionId}`);
                if (r.data.payment_status === "paid") {
                    setPayStatus("paid");
                    api.get(`/bookings/${id}`).then((res) => setBooking(res.data));
                    return;
                }
                if (r.data.status === "expired") {
                    setPayStatus("expired");
                    return;
                }
            } catch {}
            if (attempts < 6) setTimeout(poll, 2000);
            else setPayStatus("timeout");
        };
        poll();
    }, [sessionId, id]);

    return (
        <div data-testid="booking-success-page" className="min-h-screen bg-cream">
            <Navbar variant="solid" />
            <section className="pt-32 pb-24 px-6 md:px-12">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border border-gold bg-white mb-8">
                        <CheckCircle2 className="w-10 h-10 text-gold-dark" />
                    </div>
                    <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold-dark">— Booking Confirmed</span>
                    <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy mt-4 mb-4 tracking-tight">
                        Thank you, <span className="italic text-gold-dark">{booking?.full_name?.split(" ")[0] || "guest"}.</span>
                    </h1>
                    <p className="font-outfit text-base md:text-lg text-muted-foreground max-w-xl mx-auto mb-10">
                        Your booking is confirmed. A confirmation has been sent to <strong>{booking?.email || "you"}</strong>. Our team will arrive on the day — payment is taken at the end of the clean (cash, card or bank transfer).
                    </p>

                    {booking && (
                        <div className="bg-white border border-cream-border p-8 md:p-10 text-left">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-gold-dark" />
                                    <span className="uppercase tracking-[0.2em] text-xs font-bold text-navy">Booking Reference</span>
                                </div>
                                <code className="font-mono text-xs text-muted-foreground" data-testid="booking-id">{booking.id.slice(0, 8).toUpperCase()}</code>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-6 font-outfit text-sm">
                                <Detail icon={Sparkles} label="Service" value={booking.service_name} />
                                <Detail icon={Calendar} label="Date & Time" value={`${booking.date} @ ${booking.time_slot}`} />
                                <Detail icon={MapPin} label="Location" value={`${booking.address}, ${booking.city}`} />
                                <Detail icon={Mail} label="Email" value={booking.email} />
                            </div>
                            <div className="gold-divider my-8" />
                            <div className="flex items-center justify-between">
                                <span className="uppercase tracking-[0.2em] text-xs font-bold text-navy">Estimated Total</span>
                                <span className="font-playfair text-3xl md:text-4xl text-gold-dark" data-testid="success-total">{formatEUR(booking.quote_total)}</span>
                            </div>
                            <p className="mt-6 text-xs italic text-muted-foreground text-center">
                                Payable on the day. We accept cash, card or bank transfer.
                            </p>
                        </div>
                    )}

                    {booking?.referral_code && <ReferralCard code={booking.referral_code} name={booking.full_name?.split(" ")[0] || "you"} />}
                    {booking?.loyalty_points_earned > 0 && <LoyaltyCard points={booking.loyalty_points_earned} email={booking.email} />}

                    <Link
                        to="/"
                        data-testid="success-home-btn"
                        className="inline-flex items-center gap-3 mt-12 bg-navy text-gold border border-gold px-8 py-4 font-outfit font-semibold text-sm uppercase tracking-wider hover:bg-gold hover:text-navy transition-colors"
                    >
                        Back to Home <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
            <Footer />
        </div>
    );
}

function Detail({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3">
            <Icon className="w-4 h-4 text-gold-dark mt-1 shrink-0" />
            <div>
                <div className="uppercase tracking-[0.15em] text-[10px] text-muted-foreground font-semibold">{label}</div>
                <div className="text-navy mt-1">{value}</div>
            </div>
        </div>
    );
}

function ReferralCard({ code, name }) {
    const shareUrl = `${window.location.origin}/book?ref=${code}`;
    const shareText = `${name} thinks you'd love Signature Shine & Co. Use my code ${code} for €10 off your first clean → ${shareUrl}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareText);
            toast.success("Referral link copied — paste it anywhere.");
        } catch {
            toast.error("Couldn't copy — long-press to select instead.");
        }
    };

    const whatsapp = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    const mailto = `mailto:?subject=${encodeURIComponent("A little something from Signature Shine & Co.")}&body=${encodeURIComponent(shareText)}`;

    return (
        <div data-testid="referral-card" className="mt-10 bg-navy text-cream p-8 md:p-10 border border-gold/40 text-left">
            <div className="flex items-center gap-2 mb-3">
                <Gift className="w-4 h-4 text-gold" />
                <span className="uppercase tracking-[0.2em] text-xs font-bold text-gold">Give €10, get a free room</span>
            </div>
            <h2 className="font-playfair text-2xl md:text-3xl text-cream mb-3">Share the shine with a friend.</h2>
            <p className="font-outfit text-sm text-cream/80 mb-6">
                They save <strong className="text-gold">€10</strong> on their first Signature clean.
                You earn a <strong className="text-gold">free extra room</strong> on your next one.
            </p>
            <div className="bg-cream text-navy px-5 py-4 flex items-center justify-between mb-5" data-testid="referral-code-box">
                <span className="font-mono tracking-[0.3em] text-lg" data-testid="referral-code">{code}</span>
                <button
                    onClick={copy}
                    data-testid="copy-referral-btn"
                    className="inline-flex items-center gap-2 bg-navy text-gold px-3 py-2 font-outfit text-xs uppercase tracking-wider hover:bg-gold hover:text-navy transition-colors"
                >
                    <Copy className="w-3.5 h-3.5" /> Copy link
                </button>
            </div>
            <div className="flex flex-wrap gap-3">
                <a href={whatsapp} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp"
                    className="inline-flex items-center gap-2 border border-gold/60 text-gold px-4 py-2 font-outfit text-xs uppercase tracking-wider hover:bg-gold hover:text-navy transition-colors">
                    Share on WhatsApp
                </a>
                <a href={mailto} data-testid="share-email"
                    className="inline-flex items-center gap-2 border border-gold/60 text-gold px-4 py-2 font-outfit text-xs uppercase tracking-wider hover:bg-gold hover:text-navy transition-colors">
                    Share by email
                </a>
            </div>
        </div>
    );
}


function LoyaltyCard({ points, email }) {
    return (
        <div data-testid="loyalty-card" className="mt-8 bg-white border border-cream-border p-8 md:p-10 text-left">
            <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-gold-dark" />
                <span className="uppercase tracking-[0.2em] text-xs font-bold text-gold-dark">Loyalty rewards</span>
            </div>
            <h2 className="font-playfair text-2xl md:text-3xl text-navy mb-3">You'll earn <span className="text-gold-dark">{points} pts</span> on this clean.</h2>
            <p className="font-outfit text-sm text-muted-foreground mb-5">
                Points vest once we mark the booking complete. Redeem them for a free oven clean (200 pts), free window clean (500 pts) or €100 off (1,000 pts).
            </p>
            <Link
                to={`/rewards?email=${encodeURIComponent(email || "")}`}
                data-testid="view-rewards-btn"
                className="inline-flex items-center gap-2 border border-navy text-navy px-5 py-2.5 font-outfit text-xs uppercase tracking-wider hover:bg-navy hover:text-gold transition-colors"
            >
                View my rewards <ArrowRight className="w-3.5 h-3.5" />
            </Link>
        </div>
    );
}